import { useEffect, useState } from "react";
import { ChevronLeft, Receipt, FileDown, CreditCard, Loader2, CheckCircle2, XCircle } from "lucide-react";
import client, { extractError } from "@/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STATUS_LABEL = { DRAFT: "Draft", ISSUED: "Issued", CANCELLED: "Cancelled" };
const PAY_LABEL = { NOT_DUE: "Not due", DUE: "Payment due", PAID: "Paid", OVERDUE: "Overdue", WAIVED: "Waived" };

// Load Razorpay Checkout script exactly once, lazily.
let _checkoutLoader = null;
function loadRazorpayCheckout() {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve(true);
  if (_checkoutLoader) return _checkoutLoader;
  _checkoutLoader = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return _checkoutLoader;
}

function isPayable(inv) {
  return (
    inv &&
    inv.invoice_type === "FACILITATION" &&
    inv.status === "ISSUED" &&
    !inv.paid_at &&
    (inv.total || 0) > 0
  );
}

function InvoiceDetail({ id, onBack }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [payState, setPayState] = useState("idle"); // idle | loading | processing | success | failed | cancelled
  const [payMessage, setPayMessage] = useState("");

  const load = () => client.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((err) => setError(extractError(err)));
  useEffect(() => { load(); }, [id]);

  const handlePay = async () => {
    setPayState("loading");
    setPayMessage("");
    try {
      const ok = await loadRazorpayCheckout();
      if (!ok || typeof window === "undefined" || !window.Razorpay) {
        setPayState("failed");
        setPayMessage("Unable to load Razorpay Checkout. Please try again.");
        return;
      }
      const orderResp = await client.post(`/invoices/${inv.id}/razorpay/order`);
      const order = orderResp.data;
      setPayState("processing");
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Barter India",
        description: `Facilitator invoice ${inv.invoice_number}`,
        order_id: order.razorpay_order_id,
        prefill: { name: inv.recipient_name || "" },
        notes: { invoice_number: inv.invoice_number, invoice_id: inv.id },
        theme: { color: "#0f172a" },
        handler: async (rzp) => {
          try {
            const verifyResp = await client.post(`/invoices/${inv.id}/razorpay/verify`, {
              razorpay_order_id: rzp.razorpay_order_id,
              razorpay_payment_id: rzp.razorpay_payment_id,
              razorpay_signature: rzp.razorpay_signature,
            });
            if (verifyResp.data?.status === "PAID") {
              setPayState("success");
              setPayMessage("Payment successful. Invoice marked as paid.");
              await load();
            } else {
              setPayState("failed");
              setPayMessage("Payment verification did not complete. Please contact support.");
            }
          } catch (err) {
            setPayState("failed");
            setPayMessage(extractError(err, "Payment verification failed."));
          }
        },
        modal: {
          ondismiss: () => {
            setPayState("cancelled");
            setPayMessage("Payment cancelled. You can try again anytime.");
          },
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (resp) => {
        setPayState("failed");
        setPayMessage(resp?.error?.description || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      setPayState("failed");
      setPayMessage(extractError(err, "Unable to start payment."));
    }
  };

  if (error) return <div className="form-error">{error}</div>;
  if (!inv) return <div className="loading-block">Loading…</div>;
  const payable = isPayable(inv);
  const isSubscription = inv.invoice_type === "SUBSCRIPTION";
  const invoiceKind = isSubscription
    ? "SUBSCRIPTION TAX INVOICE"
    : `FACILITATION FEE INVOICE${inv.facilitator_role ? ` — ${inv.facilitator_role}` : ""}`;
  return (
    <div className="admin-company-detail" data-testid="invoice-detail">
      <button type="button" className="link-button back" onClick={onBack}><ChevronLeft size={14} /> Back to billing</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow" data-testid="invoice-detail-type">{invoiceKind}</p>
          <h2>{inv.invoice_number}</h2>
          <p className="muted">{isSubscription
            ? "This paid tax invoice covers your Barter India subscription membership fee."
            : <>This invoice covers Barter India's <strong>facilitation service only</strong>. The underlying trade is invoiced directly between the buyer and the seller.</>}</p>
        </div>
        <div className="chip-stack">
          <span className={`status-chip status-${inv.status.toLowerCase()}`} data-testid="invoice-status-chip">{STATUS_LABEL[inv.status] || inv.status}</span>
          <span className="status-chip" data-testid="invoice-payment-chip">{PAY_LABEL[inv.payment_status || inv.facilitator_fee?.payment_status] || "—"}</span>
          {payable && (
            <button type="button" className="primary-button" data-testid="pay-now-button"
                    onClick={handlePay}
                    disabled={payState === "loading" || payState === "processing" || payState === "success"}>
              {payState === "loading" || payState === "processing" ? <Loader2 size={13} className="spin" /> : <CreditCard size={13} />}
              {payState === "success" ? " Paid" : ` Pay ₹${inv.total.toLocaleString("en-IN")} now`}
            </button>
          )}
          <button type="button" className="secondary-button" data-testid="download-invoice-pdf"
            onClick={() => window.open(`${API}/api/invoices/${inv.id}/pdf`, "_blank")}><FileDown size={13} /> Download PDF</button>
        </div>
      </header>
      {payState === "success" && (
        <div className="form-success" data-testid="pay-success-banner"><CheckCircle2 size={14} /> {payMessage}</div>
      )}
      {payState === "failed" && (
        <div className="form-error" data-testid="pay-failed-banner"><XCircle size={14} /> {payMessage}</div>
      )}
      {payState === "cancelled" && (
        <div className="form-note" data-testid="pay-cancelled-banner">{payMessage}</div>
      )}
      <section className="detail-grid">
        <article className="wide">
          <p className="eyebrow">FROM</p>
          <p><strong>{inv.issuer_name}</strong></p>
          <p className="muted small">{inv.issuer_address}</p>
        </article>
        <article className="wide">
          <p className="eyebrow">TO</p>
          <p><strong>{inv.recipient_name}</strong></p>
          <p className="muted small">{inv.recipient_address}</p>
          {inv.recipient_gstin && <p className="muted small">GSTIN: {inv.recipient_gstin}</p>}
        </article>
      </section>
      {!isSubscription && <section className="section-block">
        <p className="eyebrow">UNDERLYING TRADE</p>
        <ul className="finance-list">
          <li><span>Trade value (buyer ↔ seller)</span><strong>₹{inv.facilitator_fee?.trade_value?.toLocaleString("en-IN")}</strong></li>
          <li><span>Reference transaction</span><strong>{inv.transaction?.id?.slice(0, 8)}</strong></li>
          <li><span>Listing</span><strong>{inv.transaction?.listing_title || "—"}</strong></li>
        </ul>
      </section>}
      <section className="section-block">
        <p className="eyebrow">{isSubscription ? "SUBSCRIPTION MEMBERSHIP SERVICE" : "BARTER INDIA FACILITATOR SERVICE"}</p>
        <ul className="finance-list">
          <li><span>{isSubscription ? inv.line_item_description : `Facilitator fee (${inv.facilitator_fee?.fee_rate_percent}%)`}</span><strong data-testid="invoice-subtotal">₹{inv.subtotal.toLocaleString("en-IN")}</strong></li>
          {inv.is_interstate === true ? (
            <li><span>IGST ({inv.gst_rate_percent}%)</span><strong data-testid="invoice-igst">₹{(inv.igst_amount ?? inv.gst_amount).toLocaleString("en-IN")}</strong></li>
          ) : inv.is_interstate === false ? (
            <>
              <li><span>CGST ({inv.gst_rate_percent / 2}%)</span><strong data-testid="invoice-cgst">₹{(inv.cgst_amount ?? 0).toLocaleString("en-IN")}</strong></li>
              <li><span>SGST ({inv.gst_rate_percent / 2}%)</span><strong data-testid="invoice-sgst">₹{(inv.sgst_amount ?? 0).toLocaleString("en-IN")}</strong></li>
            </>
          ) : (
            <li><span>GST ({inv.gst_rate_percent}%)</span><strong data-testid="invoice-gst-legacy">₹{inv.gst_amount.toLocaleString("en-IN")}</strong></li>
          )}
          <li><span>Total GST ({inv.gst_rate_percent}%)</span><strong data-testid="invoice-total-gst">₹{inv.gst_amount.toLocaleString("en-IN")}</strong></li>
          <li className="total"><span>{isSubscription ? "Total amount paid" : "Total facilitator fee payable"}</span><strong data-testid="invoice-total">₹{inv.total.toLocaleString("en-IN")}</strong></li>
        </ul>
      </section>
      {isSubscription && inv.payment && (
        <section className="section-block" data-testid="subscription-payment-details">
          <p className="eyebrow">PAYMENT</p>
          <ul className="finance-list">
            <li><span>Method</span><strong>{inv.payment.method}</strong></li>
            <li><span>Reference</span><strong>{inv.payment.reference}</strong></li>
            <li><span>Amount paid</span><strong>₹{inv.payment.amount.toLocaleString("en-IN")}</strong></li>
          </ul>
        </section>
      )}
      {(inv.issued_at || inv.due_at || inv.paid_at) && (
        <section className="section-block">
          <p className="eyebrow">TIMELINE</p>
          <ul className="finance-list">
            {inv.issued_at && <li><span>Issued</span><strong>{new Date(inv.issued_at).toLocaleString()}</strong></li>}
            {inv.due_at && <li><span>Due</span><strong>{new Date(inv.due_at).toLocaleString()}</strong></li>}
            {inv.paid_at && <li><span>Paid</span><strong data-testid="invoice-paid-at">{new Date(inv.paid_at).toLocaleString()}</strong></li>}
            {inv.cancelled_at && <li><span>Cancelled</span><strong>{new Date(inv.cancelled_at).toLocaleString()}</strong></li>}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function BillingPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try { const r = await client.get("/invoices"); setItems(r.data || []); }
    catch (err) { setError(extractError(err, "Unable to load invoices.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (detail) return <InvoiceDetail id={detail} onBack={() => { setDetail(null); load(); }} />;

  return (
    <div className="my-listings-page" data-testid="billing-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">TAX INVOICES</p>
          <h3>Subscription and facilitation invoices</h3>
          <p className="muted small">Barter India tax invoices remain separate from seller-to-buyer commercial invoices.</p>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="billing-table">
          <thead><tr><th>Invoice #</th><th>Type</th><th>Reference</th><th>Taxable amount</th><th>GST</th><th>Total</th><th>Payment</th><th>Issued</th><th>Due</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} className="empty-row">No invoices yet.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`billing-row-${r.id}`}>
                <td><strong>{r.invoice_number}</strong></td>
                <td><span className="status-chip" data-testid={`billing-invoice-type-${r.id}`}>{r.invoice_type === "SUBSCRIPTION" ? "Subscription" : `Facilitation${r.facilitator_role ? ` — ${r.facilitator_role}` : ""}`}</span></td>
                <td><small>{r.transaction_id?.slice(0, 8) || r.line_item_description || "—"}</small></td>
                <td>₹{r.subtotal.toLocaleString("en-IN")}</td>
                <td>₹{r.gst_amount.toLocaleString("en-IN")}</td>
                <td><strong>₹{r.total.toLocaleString("en-IN")}</strong></td>
                <td><span className={`status-chip status-${r.payment_status?.toLowerCase() || r.status.toLowerCase()}`}>{PAY_LABEL[r.payment_status] || r.payment_status}</span></td>
                <td><small>{r.issued_at ? new Date(r.issued_at).toLocaleDateString() : "—"}</small></td>
                <td><small>{r.due_at ? new Date(r.due_at).toLocaleDateString() : "—"}</small></td>
                <td><button type="button" className="link-button" data-testid={`open-invoice-${r.id}`} onClick={() => setDetail(r.id)}><Receipt size={12} /> View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
